import { MPCGridController } from "./grid-controller";
import { ProductComparisonModel, Product, PageViewContext, ProductStatisticsContext } from "../../../Models/Product.generated";
import { StatisticsResponse } from "../../../Models/StatisticsResponse";
import { MPCLocalStorageService } from "../local-storage-service";
import { MPCNavigationService } from "../helpers/navigationService";
import { MPCStickyHeaderService } from "./stickyHeaderService";
import { MPCDebugController } from "./debug-controller";
import { MPCDeviceDetectionHelper } from "../helpers/deviceDetectionHelper";
import { MPCComparePageButtonUrlUpdater } from "../helpers/comparePageButtonUrlUpdater";
import { MpcSettingsModule } from "../helpers/MpcSettings";
import { NlVanillajax, INlVanillajaxParams } from "../helpers/vanillajax";
const RecommendIcon = require("../../images/recommend.svg") as string;
const ScaleIcon2021 = require("../../images/scale-2021.svg") as string;
const PrintIcon2021 = require("../../images/print-2021.svg") as string;
import 'url-search-params-polyfill';

namespace MPC {
    import GridController = MPCGridController.GridController;
    import ProductComparisonModelManager = MPCLocalStorageService.ProductComparisonModelManager;
    import NavigationService = MPCNavigationService.NavigationService;
    import StickyHeaderService = MPCStickyHeaderService.StickyHeaderService;
    import DeviceDetectionHelper = MPCDeviceDetectionHelper.DeviceDetectionHelper;
    import ComparePageButtonUrlUpdater = MPCComparePageButtonUrlUpdater.ComparePageButtonUrlUpdater;
    import MpcSettings = MpcSettingsModule.MpcSettings;
    import DebugController = MPCDebugController.DebugController;

    export class ComparisonPageController {
        private productComparisonModelManager: ProductComparisonModelManager;
        private static _instance: ComparisonPageController;
        private readonly scope: HTMLElement;
        private navigationService: NavigationService;
        private stickyHeader: StickyHeaderService;
        private deviceDetectionHelper : DeviceDetectionHelper;
        private comparePageButtonUrlUpdater: ComparePageButtonUrlUpdater;
        private urlParams: URLSearchParams;
        private grid: HTMLElement;
        private mpcPrintPage: HTMLElement;

        static get instance() {
            return this._instance || (this._instance = new this());
        }

        public constructor() {
            this.scope = document.querySelector('.mpc-scope');
            this.grid = this.scope.querySelector('.grid');
            if (!this.scope && !this.grid){
                return;
            }

            this.init();
        }

        private init(skipNavigation : boolean = false): void  {
            // because of comparison page is a content-service, 
            // It does not trigger legacy sniff feature which fullyFills user device and browser information.
            // So we have to do it by our self:
            this.deviceDetectionHelper = DeviceDetectionHelper.instance;
            MpcSettings.instance.init(this.scope);
            this.productComparisonModelManager = ProductComparisonModelManager.instance;
            DebugController.instance.init(this.scope);

            this.productComparisonModelManager.getModel().then(model => {
                if (this.checkComparePageUrl(model))
                    return;

                // removed because of ESPP-4706
                // if(!this.deviceDetectionHelper.isMobileShop())
                //     this.deviceDetectionHelper.adaptLegacySniff();

                this.urlParams = new URLSearchParams(location.search);
                this.mpcPrintPage = this.scope.querySelector('.mpc-print-page');
                const navigationContainer: HTMLElement = this.scope.querySelector('.navigation-container');
                if (!skipNavigation) {
                    this.navigationService = NavigationService.instance;
                    this.navigationService.init(navigationContainer, 'data-pcp-navigation-key', null);
                }

                this.comparePageButtonUrlUpdater = ComparePageButtonUrlUpdater.instance;
                this.comparePageButtonUrlUpdater.init(this.scope);

                new GridController(this.scope);
            
                this.checkCurrentLocalStorage(model);
                this.stickyHeader = new StickyHeaderService();

                this.loadComparisonPageIcons();
                this.registerEvents();

                //when navigating to comparison page, unmark item in mobile header
                if (window.shell)                
                    window.shell.publishTo('ESMKT.MegaMenu.UnmarkItem', "mpc");
            });
        }

        private loadComparisonPageIcons(): void {
            this.scope.querySelector('.article-recommendation-icon').innerHTML = RecommendIcon;
            this.scope.querySelector('.mpc-print-icon').innerHTML = PrintIcon2021;
            
            if (this.deviceDetectionHelper.isMobileShop()) {
                this.scope.querySelector('.title-icon').innerHTML = ScaleIcon2021;
            }
        }

        private logPageLoadEvent(shownProducts: ProductStatisticsContext[], rootCategoryName: string): void {
            if (shownProducts.length == 0 || !rootCategoryName) {
                return; //we don't log an empty page
            }

            // Call statistics controller via POST ajax (because page maybe served from cache/cdn and we wouldn't log it)
            let params: INlVanillajaxParams = <INlVanillajaxParams>{};
            params.url = MpcSettingsModule.MpcSettings.instance.apiUrl + "Statistics/PageLoad";

            let context = {} as PageViewContext;
            context.rootCategoryName = rootCategoryName;
            context.viewedProducts = shownProducts;
            params.data = JSON.stringify(context);
            
            let nlVanillajax = new NlVanillajax(params);
            nlVanillajax.postWithPromise()
                .then((response: StatisticsResponse) => {
                    if (response.correlationId) {
                        document.documentElement.setAttribute("data-page-view-log-data-id", response.correlationId);
                    }
                    if (!response.success) {
                        console.error('error processing statistics ' + response.message);
                    }
                })
                .catch((err) => {
                    console.error('Could not post statistics', err.statusText);
                    throw err;
                });
        }

        private checkCurrentLocalStorage(model: ProductComparisonModel): void {
            if (model) {
                const categoryParam = this.urlParams.get('category');
                const categoryFromUrl: string = categoryParam ? decodeURIComponent(categoryParam) : null;
                const orderOfCategoryFromUrl = parseInt(this.scope.querySelector('.navigation-container').getAttribute("data-navigation-order"));

                if (model.clearStoredEmptyNavigationTabs) {
                    this.productComparisonModelManager.removeStoredEmptyNavigationTabs();
                    this.productComparisonModelManager.setClearStoredEmptyNavigationTabs(false);
                } else {
                    const emptyNavigationTabs = model.emptyNavigationTabs;

                    // create empty navigation tabs:
                    if (emptyNavigationTabs) {
                        for (const emptyNavigationTab of emptyNavigationTabs) {
                            this.navigationService.createOrUpdateNavigationTab(emptyNavigationTab.name, 0,
                                emptyNavigationTab.navigationOrder, null, true);
                        }
                    }
                }

                // create navTabs according tp localStorage content
                for (const storageProduct of model.products) {

                    // but only for categories that are not from the URL,
                    // because when you deepLink (mail-recommendation or bookmark) the localStorage might have conflicting data
                    if (storageProduct.categoryName === categoryFromUrl) {
                        continue;
                    }

                    this.navigationService.createOrUpdateNavigationTab(storageProduct.categoryName, 1,
                        storageProduct.navigationOrder, storageProduct.articleId.salesArticleVariantKey);
                }

                let savKeys = this.urlParams.getAll('savKeys');
                // add articles from URL
                for (const savKey of savKeys) {
                    this.navigationService.createOrUpdateNavigationTab(categoryFromUrl, 1,
                        orderOfCategoryFromUrl, savKey);
                }

                // log statistics of shown articles (i.e. those in the url for the current page/category root)
                const shownProducts = new Array<ProductStatisticsContext>();

                let fromStorage = false;
                for (const savKey of savKeys) {
                    for (const storageProduct of model.products) {
                        if (storageProduct.categoryName === categoryFromUrl && storageProduct.articleId.salesArticleVariantKey === savKey) {
                            shownProducts.push(this.map(storageProduct));
                            fromStorage = true;
                            break;
                        }
                    }

                    if (!fromStorage) {
                        const context = {} as ProductStatisticsContext;
                        context.salesArticleVariantKey = savKey;
                        context.origin = "Deeplink";
                        context.originNavigationKeyPath = "Unknown";
                        shownProducts.push(context);
                    }
                }

                // log page load event
                this.logPageLoadEvent(shownProducts, categoryFromUrl);

                this.navigationService.markActiveNavigationTab(categoryFromUrl);
                this.navigationService.updateSelectCategoryButtonVisibility();
            }
        }

        private map(source: Product): ProductStatisticsContext {
            const context = {} as ProductStatisticsContext;
            context.salesArticleVariantKey = source.articleId.salesArticleVariantKey;
            context.origin = source.origin;
            context.originNavigationKeyPath = source.originNavigationKeyPath;
            return context;
        }

        private registerEvents(): void {
            this.stickyHeader.init(this.scope);

            if(this.mpcPrintPage)
                this.mpcPrintPage.addEventListener('click', () => { this.preparePrintPage(); });

            document.addEventListener('DOMContentLoaded', () => {
                this.setNavTabFocus();
            });
        }

        private setNavTabFocus(): void {
            // focus to first navigation tab:
            if(window.shell.tabNav && this.mpcPrintPage) {
                window.shell.tabNav.focus(this.mpcPrintPage);
            }
        }

        private checkComparePageUrl(model: ProductComparisonModel): boolean {
            // check url for sav keys:
            const urlParams: URLSearchParams = new URLSearchParams(location.search);
            let currentUrl: string = window.location.href;
            const savKeysExists: number = currentUrl.indexOf('&savKeys');
            let urlParam: string;
            let categoryFromUrl: string = urlParams.get('category');

            // if it does not exists load them from local storage by category name:
            if (savKeysExists <= 0 && model && model.products.length > 0) {
                // update pcp url:
                if (!categoryFromUrl) {
                    categoryFromUrl = model.products[0].categoryName;
                    currentUrl = currentUrl + '?category=' + categoryFromUrl;
                }

                const productsArray: Array<Product> = model.products.filter(it=>it.categoryName == categoryFromUrl);

                if (this.deviceDetectionHelper.isMobileShop) {
                    this.productComparisonModelManager.clearCarouselScrollIndex();
                    this.productComparisonModelManager.clearLockedColumnIndex();
                }

                if(productsArray.length > 0) {
                    for(const product of productsArray) {
                        urlParam = '&savKeys=' + product.articleId.salesArticleVariantKey;
                        currentUrl = currentUrl + urlParam;
                    }

                    // reload page with updated url:
                    location.replace(currentUrl);
                    return true;
                }
            }

            return false;
        }

        private preparePrintPage(): void {
            const printPageLink: string = this.mpcPrintPage.getAttribute('data-print-page-link');
            const clone: HTMLElement = <HTMLScriptElement>this.grid.cloneNode(true);
            clone.classList.add('flex-grid');

            this.productComparisonModelManager.storePrintPageContent(clone);

            if(navigator.userAgent.indexOf('MSIE')!==-1 || navigator.appVersion.indexOf('Trident/') > -1) {
                window.open(printPageLink); // for ie11
            }
            else {
                window.open(printPageLink, '_blank', 'noopener');
            }
        }
    }
}

MPC.ComparisonPageController.instance;